import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getSystemSettings,
  deleteSystemSetting,
  type SystemSetting,
  type SystemSettingFilters,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';
import AdminNav from './AdminNav';

const CATEGORIES = ['general', 'email', 'security', 'appearance'];

export default function SystemSettingListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: SystemSettingFilters = { ...params };
      if (categoryFilter) filters.category = categoryFilter;
      return getSystemSettings(filters);
    },
    [categoryFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('admin.confirm_delete_setting'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteSystemSetting(id);
            message.success(t('admin.setting_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<SystemSetting> = useMemo(
    () => [
      {
        title: t('admin.field.key'),
        dataIndex: 'key',
        key: 'key',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('admin.field.value'),
        dataIndex: 'value',
        key: 'value',
        ellipsis: true,
      },
      {
        title: t('admin.field.category'),
        dataIndex: 'category',
        key: 'category',
        width: 120,
        render: (cat: string) => (
          <Tag>{t(`admin.category_options.${cat}`, cat)}</Tag>
        ),
      },
      {
        title: t('admin.field.value_type'),
        dataIndex: 'value_type',
        key: 'value_type',
        width: 100,
        render: (vt: string) => t(`admin.value_type_options.${vt}`, vt),
      },
      {
        title: t('admin.field.is_public'),
        dataIndex: 'is_public',
        key: 'is_public',
        width: 80,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('admin.field.updated_by'),
        dataIndex: 'updated_by',
        key: 'updated_by',
        width: 120,
        ellipsis: true,
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 100,
        render: (_: unknown, record: SystemSetting) => (
          <Space size="small">
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/admin/system-settings/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('admin.field.category')}
        value={categoryFilter}
        onChange={(v) => { setCategoryFilter(v); gridRef.current?.reload(); }}
        style={{ width: 150 }}
        options={CATEGORIES.map((c) => ({ value: c, label: t(`admin.category_options.${c}`) }))}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/system-settings/new')}
        >
          {t('admin.create_system_setting_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AdminNav />
      <h2>{t('admin.system_settings_title')}</h2>
      <DataGrid<SystemSetting>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('admin.search_settings_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
